import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  createSubscriptionPlan,
  listSubscriptionPlans,
  patchSubscriptionPlan,
} from '../api/endpoints/subscriptions';
import type { SubscriptionPlan, SubscriptionPlanKind } from '../api/schemas';
import { ConfirmAction } from '../components/ConfirmAction';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { FormField } from '../components/FormField';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { formatMoneyCents } from '../lib/format';
import { modeForCategory } from '../lib/mode';
import { packagesEmpty, packageWords, toolTitle } from '../lib/toolCopy';
import { useTools } from '../lib/useTools';
import { centsToDollarsInput, parsePriceInput } from '../lib/money';
import { useMutationAttempt } from '../lib/use-mutation-attempt';

const PLANS_QUERY_KEY = ['business-subscription-plans'];

export function PlanesPage() {
  const queryClient = useQueryClient();
  // Planes at a studio, Paquetes at a barbería, Membresías at a consultorio: the nav's own word.
  const { category } = useTools();
  const empty = packagesEmpty(category);
  const words = packageWords(category);
  const plansQuery = useQuery({
    queryKey: PLANS_QUERY_KEY,
    queryFn: () => listSubscriptionPlans(true),
  });

  const [name, setName] = useState('');
  const [kind, setKind] = useState<SubscriptionPlanKind>('pack');
  const [creditCount, setCreditCount] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [validityDays, setValidityDays] = useState('30');
  const [priceError, setPriceError] = useState<string | undefined>();
  const createMutation = useMutationAttempt({
    mutationFn: createSubscriptionPlan,
    exito: 'Plan creado',
    onSuccess: () => {
      setName('');
      setCreditCount('');
      setPriceInput('');
      setValidityDays('30');
      setPriceError(undefined);
      void queryClient.invalidateQueries({ queryKey: PLANS_QUERY_KEY });
    },
  });
  const creating = createMutation.isPending || createMutation.uncertain;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (createMutation.uncertain) { createMutation.retry(); return; }
    const price = parsePriceInput(priceInput);
    if (!price.ok) {
      setPriceError(price.message);
      return;
    }
    setPriceError(undefined);
    createMutation.mutate({ name, kind, ...(kind === 'pack' ? { creditCount: Number(creditCount) } : {}),
      priceCents: price.cents, validityDays: Number(validityDays) });
  }

  const plans = plansQuery.data?.data ?? [];
  const active = plans.filter((plan) => plan.isActive);
  const retired = plans.filter((plan) => !plan.isActive);

  return (
    <main>
      <PageHeader
        title={toolTitle('planes', category)}
        description={
          category && modeForCategory(category) === 'classes'
            ? 'Paquetes de clases y pases ilimitados que vendes a tus clientes.'
            : 'Lo que tus clientes pagan por adelantado: cada visita se descuenta de su saldo.'
        }
      />
      {plansQuery.isLoading ? <LoadingState label="Cargando planes…" /> : null}
      {plansQuery.isError ? <ErrorBanner message={errorMessage(plansQuery.error)} /> : null}

      {plansQuery.data ? (
        plans.length === 0 ? (
          <EmptyState title={empty.title} description={empty.description} />
        ) : (
          <>
            <ul className="rows">
              {active.map((plan) => (
                <li key={plan.id}>
                  <PlanRow plan={plan} visits={words.visits} />
                </li>
              ))}
            </ul>
            {retired.length > 0 ? (
              <section className="section" aria-label="Planes retirados">
                <h2 className="section-label">Retirados</h2>
                <p className="row-sub">
                  Ya no se venden. Las suscripciones que alguien compró siguen valiendo.
                </p>
                <ul className="rows">
                  {retired.map((plan) => (
                    <li key={plan.id}>
                      <PlanRow plan={plan} visits={words.visits} />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        )
      ) : null}

      <section className="section" aria-label={words.create}>
        <h2 className="section-label">{words.create}</h2>
        <form className="card form-grid cols-2" onSubmit={handleSubmit}>
          <FormField label="Nombre" htmlFor="plan-name" className="span-2">
            <input
              id="plan-name"
              disabled={creating}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Paquete 10 clases"
              required
            />
          </FormField>
          <FormField label="Tipo" htmlFor="plan-kind">
            <select
              id="plan-kind"
              disabled={creating}
              value={kind}
              onChange={(event) => setKind(event.target.value as SubscriptionPlanKind)}
            >
              <option value="pack">{words.pack}</option>
              <option value="unlimited">Pase ilimitado</option>
            </select>
          </FormField>
          {kind === 'pack' ? (
            <FormField label={words.visits} htmlFor="plan-credits" hint={words.visitsHint}>
              <input
                id="plan-credits"
                disabled={creating}
                type="number"
                min={1}
                max={200}
                value={creditCount}
                onChange={(event) => setCreditCount(event.target.value)}
                required
              />
            </FormField>
          ) : (
            <FormField label={words.visits} htmlFor="plan-credits-none" hint="Un pase ilimitado no lleva cuenta">
              <input id="plan-credits-none" value="Sin límite" disabled />
            </FormField>
          )}
          <FormField label="Precio" htmlFor="plan-price" error={priceError} hint="En dólares, por ejemplo 120.00">
            <input
              id="plan-price"
              disabled={creating}
              inputMode="decimal"
              placeholder="120.00"
              value={priceInput}
              onChange={(event) => setPriceInput(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Vigencia (días)" htmlFor="plan-validity" hint="Desde el día de la compra">
            <input
              id="plan-validity"
              disabled={creating}
              type="number"
              min={1}
              max={730}
              value={validityDays}
              onChange={(event) => setValidityDays(event.target.value)}
              required
            />
          </FormField>
          {createMutation.isError ? <ErrorBanner message={errorMessage(createMutation.error)} /> : null}
          <button type="submit" className="btn" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creando…' : words.create}
          </button>
        </form>
      </section>
    </main>
  );
}

type PlanRowProps = { plan: SubscriptionPlan; visits: string };

function PlanRow({ plan, visits }: PlanRowProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(plan.name);
  const [creditCount, setCreditCount] = useState(plan.creditCount?.toString() ?? '');
  const [priceInput, setPriceInput] = useState(centsToDollarsInput(plan.priceCents));
  const [validityDays, setValidityDays] = useState(plan.validityDays.toString());
  const [priceError, setPriceError] = useState<string | undefined>();
  const patchMutation = useMutationAttempt({
    mutationFn: (input: Parameters<typeof patchSubscriptionPlan>[1], key: string) =>
      patchSubscriptionPlan(plan.id, input, key),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: PLANS_QUERY_KEY });
    },
  });
  const saving = patchMutation.isPending || patchMutation.uncertain;

  function handleSave(event: FormEvent) {
    event.preventDefault();
    if (patchMutation.uncertain) { patchMutation.retry(); return; }
    const price = parsePriceInput(priceInput);
    if (!price.ok) {
      setPriceError(price.message);
      return;
    }
    setPriceError(undefined);
    patchMutation.mutate({
      name,
      ...(plan.kind === 'pack' ? { creditCount: Number(creditCount) } : {}),
      priceCents: price.cents,
      validityDays: Number(validityDays),
    });
  }

  if (editing) {
    return (
      <form className="card form-grid cols-2" onSubmit={handleSave}>
        <FormField label="Nombre" htmlFor={`plan-${plan.id}-name`} className="span-2">
          <input
            id={`plan-${plan.id}-name`}
            disabled={saving}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </FormField>
        {plan.kind === 'pack' ? (
          <FormField label={visits} htmlFor={`plan-${plan.id}-credits`}>
            <input
              id={`plan-${plan.id}-credits`}
              disabled={saving}
              type="number"
              min={1}
              max={200}
              value={creditCount}
              onChange={(event) => setCreditCount(event.target.value)}
              required
            />
          </FormField>
        ) : null}
        <FormField label="Precio" htmlFor={`plan-${plan.id}-price`} error={priceError}>
          <input
            id={`plan-${plan.id}-price`}
            disabled={saving}
            inputMode="decimal"
            value={priceInput}
            onChange={(event) => setPriceInput(event.target.value)}
            required
          />
        </FormField>
        <FormField label="Vigencia (días)" htmlFor={`plan-${plan.id}-validity`}>
          <input
            id={`plan-${plan.id}-validity`}
            disabled={saving}
            type="number"
            min={1}
            max={730}
            value={validityDays}
            onChange={(event) => setValidityDays(event.target.value)}
            required
          />
        </FormField>
        {patchMutation.isError ? <ErrorBanner message={errorMessage(patchMutation.error)} /> : null}
        <div className="action-pair span-2">
          <button type="submit" className="btn" disabled={patchMutation.isPending}>
            {patchMutation.isPending ? 'Guardando…' : 'Guardar'}
          </button>
          <button type="button" className="btn secondary" disabled={saving} onClick={() => setEditing(false)}>
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="row">
      <div className="grow">
        <p className="row-title">{plan.name}</p>
        <p className="row-sub">
          {planShape(plan)} · {formatMoneyCents(plan.priceCents)} · vence a los {plan.validityDays} días
        </p>
        {patchMutation.isError ? <ErrorBanner message={errorMessage(patchMutation.error)} /> : null}
      </div>
      <div className="action-pair">
        <button type="button" className="btn small ghost" disabled={saving} onClick={() => setEditing(true)}>
          Editar
        </button>
        {plan.isActive ? (
          <ConfirmAction
            label="Retirar"
            confirmLabel="Retirar plan"
            /* Quiet on purpose: retiring is rare and reversible, so it must not
               outshout "Crear plan", the only primary action on this page. */
            variant="secondary"
            pending={patchMutation.isPending}
            onConfirm={() => patchMutation.mutate({ isActive: false })}
          />
        ) : (
          <button
            type="button"
            className="btn small secondary"
            disabled={patchMutation.isPending}
            onClick={() => patchMutation.mutate({ isActive: true })}
          >
            Reactivar
          </button>
        )}
      </div>
    </div>
  );
}

function planShape(plan: SubscriptionPlan): string {
  if (plan.kind === 'unlimited') return 'Pase ilimitado';
  return `${plan.creditCount} clases`;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos cargar los planes.';
}
